import { List, Map, OrderedMap } from 'immutable'
import { keyBy } from 'utils/immutable'
import _uniqueId from 'lodash/uniqueId'
import _isNumber from 'lodash/isNumber'
import { Observable } from 'rxjs/Observable'
import { catchError } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { visibility$ } from 'utils/visibility'
import { UserTypes } from 'utils/UserTypes'
import { captureException } from 'utils/SentryHelpers'
import app from 'containers/app'
import * as SocketActions from 'app/modules/socket/actions'
import * as SocketConstants from 'app/modules/socket/constants'
import * as UserSelectors from 'app/modules/user/selectors'
import * as BulkUpdateConversationActions from 'app/modules/conversation/bulkUpdate/actions'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import { api as ConversationApi } from 'app/modules/conversation/api'
import * as UserPropertiesActions from 'app/modules/user/properties/actions'
import * as UserPropertiesSelectors from 'app/modules/user/properties/selectors'
import * as GatesSelectors from 'app/modules/gates/selectors'
import * as EndUserActions from 'app/modules/endUser/actions'
import * as InboxActions from 'app/modules/inbox/actions'
import * as InboxSelectors from 'app/modules/inbox/selectors'
import { hasConversationAccess } from 'app/modules/access/helpers'
import { epic as attachmentEpic } from './attachment'
import { api } from './api'
import * as actions from './actions'
import * as PlaybookActions from 'app/modules/playbook/actions'
import * as ComposerActions from 'app/modules/composer/actions'
import { TEMP_ID_KEY_PATH, TEMP_ID_KEY, parseMessage } from './models'
import { isReadByCurrentUser, makeConversationMessages, getNextPageId, firstPaginatedListLoaded, nextListPendingForConversation } from './selectors'
import * as DemoConstants from './demodata'

function fetchFirstMessagesForContext(action$, { getState }) {
  return action$
    .ofType(actions.fetchConversationMessages.toString())
    .pluck('payload')
    .filter(({ conversationId, force }) => {
      if (!!force) {
        return true
      }
      return GatesSelectors.hasDemoOrg(getState()) || !firstPaginatedListLoaded(getState(), { conversationId })
    })
    .mergeMap(({ conversationId }) => {

      if (GatesSelectors.hasDemoOrg(getState())) {
        const conversation = ConversationSelectors.makeConversation()(getState(), { conversationId })

        const messages = keyBy(DemoConstants.messages(
          UserSelectors.currentUser(getState()).toJS(),
          UserSelectors.currentOrgUsersAlphabetical(getState()).toJS(),
          conversationId,
          conversation && conversation.endUserId,
        ).map(parseMessage))

        return Observable.of(actions.receiveFirstMessages({ conversationId, messages }))
      } else {
        return api
          .fetch(conversationId, 10)
          .map((messages) => {
            return actions.receiveFirstMessages({ conversationId, messages })
          })
          .startWith(actions.fetchFirstMessagesPending({ conversationId }))
          .catch(error => Observable.of(actions.fetchFirstMessagesFailed({ conversationId, error })))
      }
    })
}

function fetchPaginatedConversationMessages(action$, { getState }) {
  return action$
    .ofType(actions.fetchConversationMessages.toString())
    .pluck('payload')
    .filter(({ conversationId, force }) => {
      if (!!force) {
        return true
      }
      return GatesSelectors.hasDemoOrg(getState()) || !firstPaginatedListLoaded(getState(), { conversationId })
    })
    .mergeMap(({ conversationId }) => {
      if (GatesSelectors.hasDemoOrg(getState())) {
        const conversation = ConversationSelectors.makeConversation()(getState(), { conversationId })

        const messages = keyBy(DemoConstants.messages(
          UserSelectors.currentUser(getState()).toJS(),
          UserSelectors.currentOrgUsersAlphabetical(getState()).toJS(),
          conversationId,
          conversation && conversation.endUserId,
        ).reverse().map(parseMessage), { acc: OrderedMap() })
        const more = false, next = false

        return Observable.of(actions.receivePaginatedMessageList({ conversationId, messages, more, next }))
      } else {
        return api
          .fetchPaginated(conversationId)
          .map(({ messages, pagination: { more, next }}) => {
            return actions.receivePaginatedMessageList({ conversationId, messages, more, next })
          })
          .startWith(actions.fetchPaginatedConversationMessagesPending({ conversationId }))
          .catch(error => Observable.of(actions.fetchPaginatedConversationMessagesFailed({ conversationId, error })))
      }
    })
}

function fetchNextPaginatedConversationMessages(action$, { getState }) {
  return action$
    .ofType(actions.fetchNextPaginatedConversationMessages.toString())
    .pluck('payload')
    .filter(({ conversationId }) => !nextListPendingForConversation(getState(), { conversationId }))
    .mergeMap(({ conversationId }) => {
      const nextPageId = getNextPageId(getState(), { conversationId })
      return api
        .fetchPaginated(conversationId, nextPageId)
        .map(({ messages, pagination: { more, next }}) => actions.receiveNextMessageList({ conversationId, messages, more, next }))
        .startWith(actions.fetchNextPaginatedConversationMessagesPending({ conversationId }))
        .catch(error => Observable.of(actions.fetchNextPaginatedConversationMessagesFailed({ conversationId, error })))
    })
}


function fetchPlaybookContext(action$, { getState }) {
  return action$
    .ofType(actions.fetchPlaybookContext.toString())
    .pluck('payload')
    .filter(({ conversationId }) => !ConversationSelectors.playbookIdByConversationId(getState()).has(conversationId))
    .mergeMap(({ conversationId }) => {
      const messages = makeConversationMessages()(getState(), { conversationId })
      const messageWithPartId = messages.find(m =>
        !!m.getIn(['attributes', 'campaignId']) ||
        !!m.getIn(['attributes', 'relatedCampaignId']) ||
        !!m.getIn(['attributes', 'interactionId'])
      )
      if (messageWithPartId) {
        const campaignId = messageWithPartId.getIn(['attributes', 'relatedCampaignId']) || messageWithPartId.getIn(['attributes', 'campaignId'])
        const interactionId = messageWithPartId.getIn(['attributes', 'interactionId'])
        return campaignId
          ? Observable.of(PlaybookActions.fetchPlaybookByCampaignId({ campaignId, conversationId }))
          : interactionId
            ? Observable.of(PlaybookActions.fetchPlaybookByInteractionId({ interactionId, conversationId }))
            : Observable.empty()
      }
      return Observable.empty()
    })
}

function sendMessageThroughApi(conversationId, messageWithId) {
  return api
    .create(conversationId, messageWithId)
    .map((message) => actions.receiveMessage({ conversationId, message }))
    .startWith(actions.createMessagePending({ conversationId, message: messageWithId }))
    .catch(error => {
      captureException(error)
      return Observable.of(actions.createMessageFailed({ conversationId, message: messageWithId, error }))
    })
}

function createMessage(action$) {
  return action$
    .ofType(actions.createMessage.toString())
    .pluck('payload')
    .mergeMap(({ message, isRetry = false }) => {
      const { conversationId } = message
      const messageWithId = isRetry ? message.set('failed', false) : message
        .setIn(TEMP_ID_KEY_PATH, _uniqueId())
        .set('createdAt', Date.now())
      return sendMessageThroughApi(conversationId, messageWithId)
    })
}

function selectBotActionEpic(action$) {
  return action$
    .ofType(actions.selectBotAction.toString())
    .pluck('payload')
    .filter(({ selectedButton }) => selectedButton.get('type') !== 'action' || !!selectedButton.getIn(['reaction', 'type']))
    .mergeMap(actionParams => {
      const button = actionParams.selectedButton
      const conversationId = actionParams.conversationId
      const messageWithId = {
        type: "EDIT",
        contentType: "EDIT",
        body: actionParams.body,
        attributes: {
          selectedBotAction: button,
          editedMessageId: actionParams.editedMessageId,
        }
      }

      return sendMessageThroughApi(conversationId, parseMessage(
        button.getIn(['reaction', 'type']) === 'delete' || button.get('type') === 'noop'
          ? { ...messageWithId, attributes: { ...messageWithId.attributes, editType: 'delete' }}
        : messageWithId
      ))
    })
}

function selectBotActionResultEpic(action$) {
  return action$
    .ofType(actions.selectBotAction.toString())
    .pluck('payload')
    .mergeMap(actionParams => {
      const button = actionParams.selectedButton
      const conversationId = actionParams.conversationId
      switch (button.get('type')) {
        case 'compose':
          return Observable.of(ComposerActions.setComposerTextToInsert({ text: button.get('value') }))
        case 'action':
          ConversationApi.buttonAction(conversationId, { button, sourceMessageId: actionParams.editedMessageId })
            .subscribe()
          return Observable.empty()
        case 'noop':
          return Observable.empty()
        case 'reply':
        default:
          const messageWithId = parseMessage({
            type: "CHAT",
            contentType: "CHAT",
            buttonResponse: {
              label: button.get('label'),
              value: button.get('value')
            },
            body: button.get('value')
          })
          return sendMessageThroughApi(conversationId, messageWithId)
      }
    })
}

function fetchEndUserForMessages(action$, { getState }) {
  return action$
    .ofType(actions.receiveFirstMessages.toString())
    .pluck('payload')
    .mergeMap(({ messages }) => {
      const ids = messages
        .filter(message => {
          return message.authorType === UserTypes.END_USER ||
                 message.authorType === UserTypes.LEAD
        })
        .map(message => message.authorId)
        .toSet()
        .toJS()
      return GatesSelectors.hasSplitEndUserChannel(getState())
        ? Observable.of(EndUserActions.fetchEndUserList({ ids })) // TODO: Cleanup after socket
        : Observable.of(EndUserActions.fetchEndUserList({ ids }))
    })
}

function markMessageRead(action$, { getState }) {
  return action$
    .ofType(actions.messageRead.toString())
    .filter(() => !GatesSelectors.hasBulkMessageRead(getState()))
    .pluck('payload')
    .filter(({ messageId }) => _isNumber(messageId)) // filter pre-messages
    .filter(({ messageId }) => !isReadByCurrentUser(messageId)(getState()))
    .mergeMap((message) => {
      if (!document.hidden) {
        return Observable.of(message)
      } else {
        return visibility$
          .filter(visible => visible)
          .map(() => message)
          .first()
      }
    })
    .mergeMap(({ messageId, messageCreatedAt, conversationId }) => {
      const user = UserSelectors.currentUser(getState())
      const recipientId = user.id
      const recipientType = user.type
      return api.markRead(messageId)
        .map(() => actions.messageReadSuccess({ messageId, messageCreatedAt, conversationId, recipientId, recipientType }))
        .startWith(actions.optimisticMessageReadSuccess({ messageId, messageCreatedAt, conversationId, recipientId, recipientType }))
        .catch(error => {
          return Observable.of(actions.messageReadFailed({ messageId, error }))
        })
    }, 10)
}

function bufferedMarkMessageRead(action$, { getState, dispatch }) {
  return action$
    .ofType(actions.messageRead.toString())
    .filter(() => GatesSelectors.hasBulkMessageRead(getState()))
    .pluck('payload')
    .filter(({ messageId }) => _isNumber(messageId)) // filter pre-messages
    .filter(({ messageId }) => !isReadByCurrentUser(messageId)(getState()))
    .mergeMap((message) => {
      if (!document.hidden) {
        return Observable.of(message)
      } else {
        return visibility$
          .filter(visible => visible)
          .map(() => message)
          .first()
      }
    })
    .map((payload) => {
      const { messageId, messageCreatedAt, conversationId } = payload
      const user = UserSelectors.currentUser(getState())
      const recipientId = user.id
      const recipientType = user.type

      dispatch(actions.optimisticMessageReadSuccess({ messageId, messageCreatedAt, conversationId, recipientId, recipientType }))

      return payload
    })
    .bufferTime(5000)
    .map((messages) => _(messages)
      .groupBy('conversationId')
      .value())
    .mergeMap((messagesGroupedByConversationid) =>
      Observable.from(_.map(messagesGroupedByConversationid, (messages, conversationId) =>
        actions.markMessageReadByConversation({ messages, conversationId: parseInt(conversationId) }))))
}

function markMessageReadByConversation(action$, { getState }) {
  return action$
    .ofType(actions.markMessageReadByConversation.toString())
    .pluck('payload')
    .mergeMap(({ messages, conversationId }) => {
      const user = UserSelectors.currentUser(getState())
      const recipientId = user.id
      const recipientType = user.type
      const messageIds = _.map(messages, 'messageId')
      return api.bulkMarkRead(conversationId, messageIds)
        .map(() => actions.bulkMessageReadSuccess({ messages, conversationId, recipientId, recipientType }))
        .catch(error => Observable.of(actions.messageReadFailed({ messageIds, error })))
    })
}


function fetchConversationToNotify(action$) {
  return action$.ofType(actions.receiveMessage.toString())
    .pluck('payload', 'message')
    .map(message => ConversationActions.fetchConversation({ conversationId: message.conversationId }))
}

function notifyMessage(action$, { getState }) {
  return action$.ofType(actions.receiveMessage.toString())
    .pluck('payload', 'message')
    .groupBy(message => message.conversationId)
    .mergeMap(conversationMessage$ => {
      const conversationFetched$ = !!ConversationSelectors.makeConversation()(getState(), { conversationId: conversationMessage$.key }) ? Observable.of(true) :
                                   action$
                                     .ofType(ConversationActions.receiveConversation.toString())
                                     .pluck('payload', 'conversation')
                                     .filter(conversation => conversation.id === conversationMessage$.key)
                                     .first()

      const userSettingsFetched$ = UserPropertiesSelectors.propertiesLoaded(getState()) ? Observable.of(true) :
                                   action$
                                     .ofType(UserPropertiesActions.receiveUserProperties.toString())
                                     .first()

      // Wait for the conversation and userProperties to be fetched before notifying
      const isNotifiable$ = Observable.forkJoin(conversationFetched$, userSettingsFetched$)

      return conversationMessage$
        .skipUntil(isNotifiable$)
        .map(actions.notifyUser)
    })
}

const createConversationEventMessageOnReceiveBulkUpdateSuccess = (action$) =>
  action$
    .ofType(BulkUpdateConversationActions.receiveBulkUpdateSuccess.toString())
    .pluck('payload')
    .map(({ inboxId, updaterId, conversationDiffs }) => {
      const filteredConversationDiffs = conversationDiffs.filter(({ previousStatus, currentStatus }) => {
        return previousStatus !== currentStatus
      })

      const timestamp = Date.now()

      const messages = filteredConversationDiffs.map(({ conversationId, currentStatus }) => {
        return parseMessage({
          inboxId,
          type: 'CONVERSATION_EVENT',
          conversationId,
          authorId: updaterId,
          authorType: UserTypes.USER,
          conversationEvent: {
            type: 'CHANGED_STATUS',
            conversationStatus: currentStatus,
          },
          createdAt: timestamp,
          attributes: Map({
            [TEMP_ID_KEY]: _uniqueId(),
          }),
          deliveryChannel: 'CHAT',
          contentType: 'CONVERSATION_EVENT',
        })
      })

      return actions.receiveBulkUpdateMessages({ messages })
    })

function pushMessage(action$, { getState }) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable
        .create((observer) => {
          channel.on('message-new', payload => {
            const state = getState()

            observer.next(actions.receiveMessage({
              message: parseMessage(payload),
              conversationId: payload.conversationId
            }))

            const currentInboxId = InboxSelectors.currentInboxId(state)
            const currentUser = UserSelectors.currentUser(state)

            if (payload.type === 'CONVERSATION_EVENT'
                && payload.conversationEvent.type === 'CHANGED_STATUS'
                && payload.inboxId === currentInboxId) {
              if (hasConversationAccess(List(payload.conversationEvent.participantIds), currentUser)) {
                observer.next(InboxActions.changeStatus({
                  id: currentInboxId,
                  status: payload.conversationEvent.conversationStatus,
                  previousStatus: payload.conversationEvent.previousStatus,
                  currentParticipants: payload.conversationEvent.currentParticipantIds,
                  currentUserId: currentUser.id,
                }))
              }
            }
          })
        })
        .takeUntil(leaveChannel$)
    })
}

function pushMessageReadReceipt(action$) {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable
        .create((observer) => {
          channel.on('message-recipient-status', (payload) => {
            observer.next(actions.receiveMessageRead(payload))
          })
        })
        .takeUntil(leaveChannel$)
    })
}

function receiveReadReceiptForCurrentUser(action$, { getState }) {
  return action$
    .ofType(actions.receiveMessageRead.toString())
    .pluck('payload')
    .filter((payload) => payload.status === 'Read')
    .filter(({ recipientType, recipientId }) => {
      const currentUser = UserSelectors.currentUser(getState())
      return recipientType === currentUser.type && recipientId === currentUser.id
    })
    .mergeMap((payload) => Observable.of(actions.messageReadSuccess(payload)))
}

export const epic = combineEpics(
  fetchFirstMessagesForContext,
  fetchPaginatedConversationMessages,
  fetchNextPaginatedConversationMessages,
  createMessage,
  markMessageRead,
  bufferedMarkMessageRead,
  markMessageReadByConversation,
  fetchEndUserForMessages,
  notifyMessage,
  fetchConversationToNotify,
  pushMessage,
  pushMessageReadReceipt,
  attachmentEpic,
  fetchPlaybookContext,
  selectBotActionEpic,
  selectBotActionResultEpic,
  receiveReadReceiptForCurrentUser,
  createConversationEventMessageOnReceiveBulkUpdateSuccess,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/message/epic.js